/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Users, Shield, FileText, GraduationCap, Archive, RefreshCw,
  Edit, Trash2, Eye, EyeOff, Search, Filter, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, Clock, AlertTriangle, Building2, CreditCard,
  History, Download, Upload, Plus, Settings, Crown, LayoutDashboard,
  BookOpen, FolderOpen, BarChart3
} from 'lucide-react';
import BackButton from '../components/BackButton';
import { getToken, getAuthHeader as getStoredAuthHeader } from '../services/tokenService';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const StatsCard = ({ title, value, icon: Icon, color, subStats }) => (
  <Card className={`border-l-4 border-l-${color}-500`}>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">{title}</CardTitle>
      <Icon className={`h-5 w-5 text-${color}-500`} />
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
      {subStats && (
        <div className="text-xs text-gray-500 mt-2 space-y-1">
          {subStats.map((stat, i) => (
            <div key={i} className="flex items-center gap-1">
              {stat.icon} {stat.label}: {stat.value}
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

// Composant de table générique
export const DataTable = ({ 
  data, 
  columns, 
  onEdit, 
  onArchive, 
  onRestore, 
  onDelete,
  selectedIds,
  setSelectedIds,
  showArchived
}) => {
  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === data.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(data.map(item => item.id));
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left">
              <input 
                type="checkbox" 
                checked={selectedIds.length === data.length && data.length > 0}
                onChange={toggleSelectAll}
                className="rounded"
              />
            </th>
            {columns.map((col, i) => (
              <th key={i} className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                {col.header}
              </th>
            ))}
            <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {data.map((item) => (
            <tr key={item.id} className={`hover:bg-gray-50 ${item.archived ? 'bg-gray-100 opacity-75' : ''}`}>
              <td className="px-4 py-3">
                <input 
                  type="checkbox" 
                  checked={selectedIds.includes(item.id)}
                  onChange={() => toggleSelect(item.id)}
                  className="rounded"
                />
              </td>
              {columns.map((col, i) => (
                <td key={i} className="px-4 py-3 text-sm">
                  {col.render ? col.render(item) : item[col.key]}
                </td>
              ))}
              <td className="px-4 py-3 text-right">
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => onEdit(item)} title="Modifier">
                    <Edit className="w-4 h-4" />
                  </Button>
                  {item.archived ? (
                    <Button size="sm" variant="ghost" onClick={() => onRestore(item.id)} title="Restaurer" className="text-green-600">
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => onArchive(item.id)} title="Archiver" className="text-orange-600">
                      <Archive className="w-4 h-4" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => onDelete(item.id)} title="Supprimer" className="text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          Aucun élément trouvé
        </div>
      )}
    </div>
  );
};

// Modal d'édition générique
export const EditModal = ({ isOpen, onClose, title, children, onSave, saving }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">{title}</h2>
          <Button variant="ghost" onClick={onClose}>✕</Button>
        </div>
        <div className="p-6">
          {children}
        </div>
        <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={onSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>
      </div>
    </div>
  );
};

